import { isClerkAPIResponseError, useClerk } from "@clerk/clerk-expo";
import type {
  SignInFirstFactor,
  SignInResource,
  SignUpResource,
} from "@clerk/types";
import type { RefObject } from "react";

import { shortBrandName } from "@/config/brand";
import { captureException, logger } from "@/sentry";
import type { SIGN_IN_FLOW } from "./SignIn";

// Hard coding USA because we only support USA at this time
// Without the country code, Twilio will infer the country code from the phone number which can be incorrect
// e.g. 2201234567 will be inferred as +220 (Gambia)
export const USA_COUNTRY_CODE = "+1";
export const PHONE_LENGTH = 10;
export const CODE_LENGTH = 6;
export const RESEND_TIMER_SECONDS = 30;

/** A 10-digit US number as the code screens show it, e.g. "🇺🇸 +1 (415) 555 0123". */
export const formatPhone = (phone: string): string =>
  `🇺🇸 ${USA_COUNTRY_CODE} (${phone.slice(0, 3)}) ${phone.slice(3, 6)} ${phone.slice(6)}`;

/**
 * The SMS opt-in shown under every phone number entry.
 *
 * :param submitAction: How the patient submits the number, e.g. "clicking 'Continue'".
 * :return: The opt-in text.
 */
export const getSmsOptInText = (submitAction: string): string =>
  `By providing my phone number and ${submitAction}, I agree to receive marketing and ` +
  `informational text messages from ${shortBrandName} at the phone number provided above. ` +
  "I understand I will receive multiple messages a month, data rates may apply. Reply STOP to " +
  "opt out.";

export type VerifyCodeResult =
  | { outcome: "verified"; sessionId: string | null }
  | { outcome: "incorrect" }
  /** `errorMessage` is Clerk's own message, or null when the error did not come from Clerk. */
  | { outcome: "failed"; errorMessage: string | null };

/**
 * A message for a failed send. Expected errors are surfaced to the patient but not reported to
 * Sentry, since they are not actionable engineering-side.
 */
const _getSendErrorMessage = (error: unknown, flow: SIGN_IN_FLOW): string => {
  if (isClerkAPIResponseError(error)) {
    if (error.errors.some((e) => e.code === "unsupported_country_code")) {
      return "We couldn't verify this phone number. Please double-check your area code and try again.";
    }
    if (error.errors.some((e) => e.code === "user_banned")) {
      return "You are no longer eligible for our services.";
    }
    if (error.status === 422) {
      return "Please enter a valid 10-digit US phone number and try again.";
    }
  }
  captureException("Error completing auth flow", { extra: { flow, error } });
  return error instanceof Error ? error.message : JSON.stringify(error);
};

/**
 * The Clerk phone-code sign-in, shared by every screen that signs a patient in by text message:
 * try a sign-up, fall back to signing in when the number is already registered, then check the
 * code. Screens own their UI and what they show for each result.
 *
 * :param signInFlowRef: Records whether this attempt is a sign-up or a sign-in. `Loading` reads it
 *   afterwards to complete the matching one, so it must outlive any remount the session swap causes.
 */
export function usePhoneCodeAuth(signInFlowRef: RefObject<SIGN_IN_FLOW>) {
  const clerk = useClerk();

  const _prepareSignInCode = async (
    supportedFirstFactors: SignInFirstFactor[] | null | undefined,
  ) => {
    const firstPhoneFactor = supportedFirstFactors?.find(
      (factor) => factor.strategy === "phone_code",
    );
    await clerk.client.signIn.prepareFirstFactor({
      strategy: "phone_code",
      phoneNumberId: firstPhoneFactor?.phoneNumberId!,
    });
  };

  /**
   * Texts a code to the number.
   *
   * :param phone: The 10-digit US number, without a country code.
   * :return: A message to show the patient when it failed, or null once the code is sent.
   */
  const sendCode = async (phone: string): Promise<string | null> => {
    const phoneNumber = USA_COUNTRY_CODE + phone;
    signInFlowRef.current = "sign-up";
    try {
      await clerk.client.signUp.create({ phoneNumber });
      await clerk.client.signUp.preparePhoneNumberVerification();
      return null;
    } catch (error) {
      const isExistingNumber =
        isClerkAPIResponseError(error) &&
        error.errors.some((e) => e.code === "form_identifier_exists");
      if (!isExistingNumber) return _getSendErrorMessage(error, "sign-up");
    }

    // This phone is already registered, so sign in instead.
    signInFlowRef.current = "sign-in";
    try {
      const { supportedFirstFactors } = await clerk.client.signIn.create({
        identifier: phoneNumber,
      });
      await _prepareSignInCode(supportedFirstFactors);
      return null;
    } catch (error) {
      // e.g. Clerk rate limiting ("Too many verification code requests")
      return _getSendErrorMessage(error, "sign-in");
    }
  };

  /**
   * Texts a fresh code for whichever flow `sendCode` started.
   *
   * :return: Clerk's message when it failed, or null once the code is sent or when the error did not
   *   come from Clerk.
   */
  const resendCode = async (): Promise<string | null> => {
    try {
      if (signInFlowRef.current === "sign-up") {
        await clerk.client.signUp.preparePhoneNumberVerification();
      } else {
        await _prepareSignInCode(clerk.client.signIn.supportedFirstFactors);
      }
      return null;
    } catch (error) {
      logger.error("Failed to resend OTP", { category: "auth", error });
      return isClerkAPIResponseError(error) ? error.message : null;
    }
  };

  /**
   * Checks the code. It does not sign the patient in: callers pass the returned session to
   * `activateSession` when their UI is ready for the session swap, which can remount screens.
   *
   * :param code: The six-digit code the patient typed.
   * :return: Whether the code was right (with its new session), wrong, or could not be checked.
   */
  const verifyCode = async (code: string): Promise<VerifyCodeResult> => {
    let verified: SignUpResource | SignInResource;
    try {
      if (signInFlowRef.current === "sign-up") {
        verified = await clerk.client.signUp.attemptPhoneNumberVerification({
          code,
        });
      } else {
        verified = await clerk.client.signIn.attemptFirstFactor({
          strategy: "phone_code",
          code,
        });
      }
    } catch (error) {
      logger.error("Failed to continue with OTP", { category: "auth", error });
      const isClerkError = isClerkAPIResponseError(error);
      const isIncorrect =
        isClerkError &&
        error.errors.some((e) => e.code === "form_code_incorrect");
      if (isIncorrect) return { outcome: "incorrect" };

      return {
        outcome: "failed",
        errorMessage: isClerkError ? error.message : null,
      };
    }

    return { outcome: "verified", sessionId: verified.createdSessionId };
  };

  /** Signs the patient in with the session `verifyCode` returned. */
  const activateSession = async (sessionId: string | null): Promise<void> => {
    await clerk.setActive({ session: sessionId });
  };

  return { sendCode, resendCode, verifyCode, activateSession };
}
